import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useConversationWebSocket } from '../hooks/useConversationWebSocket'
import { useAudioRecorder } from '../hooks/useAudioRecorder'
import { useProgressionContext } from '../context/ProgressionContext'
import '../styles/progression.css'

/**
 * Guided conversation page for structured financial data collection.
 * Uses WebSocket connection to /ws/conversation/ for voice and text interaction.
 *
 * @returns JSX.Element The conversation page
 */
const ConversationPage: React.FC = () => {
  const navigate = useNavigate()
  const { mergeFormData: mergeContextFormData, completeStep } = useProgressionContext()

  const {
    isConnected,
    messages,
    formData,
    isComplete,
    error: wsError,
    sendTextMessage,
    sendAudioData,
    reconnect,
  } = useConversationWebSocket()

  const { isRecording, startRecording, stopRecording } = useAudioRecorder()
  const [textInput, setTextInput] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const lastUserMessageRef = useRef<HTMLDivElement>(null)
  const prevMessageCountRef = useRef(0)
  const audioRef = useRef<HTMLAudioElement>(null)

  useEffect(() => {
    if (messages.length > prevMessageCountRef.current) {
      const latest = messages[messages.length - 1]
      if (latest?.role === 'user') {
        lastUserMessageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }
    prevMessageCountRef.current = messages.length
  }, [messages])

  // Play audio from latest assistant message
  useEffect(() => {
    if (messages.length === 0) return
    const lastMsg = messages[messages.length - 1]
    if (lastMsg.role === 'assistant' && lastMsg.audio && audioRef.current) {
      try {
        audioRef.current.src = `data:audio/mp3;base64,${lastMsg.audio}`
        void audioRef.current.play()
      } catch (err) {
        console.error({
          message: 'Error playing audio response',
          error: err instanceof Error ? err.message : String(err),
          context: { action: 'playAudio' },
        })
      }
    }
  }, [messages])

  // Handle conversation completion — navigate to data correction
  useEffect(() => {
    if (isComplete && formData) {
      mergeContextFormData(formData)
      completeStep(2)
      navigate('/datacorrection')
    }
  }, [isComplete, formData, mergeContextFormData, completeStep, navigate])

  const handleSendText = (e: React.FormEvent) => {
    e.preventDefault()
    if (!textInput.trim() || !isConnected) return
    sendTextMessage(textInput.trim())
    setTextInput('')
  }

  const handleStartRecording = async () => {
    try {
      await startRecording()
    } catch (err) {
      console.error({
        message: 'Failed to start recording',
        error: err instanceof Error ? err.message : String(err),
        context: { action: 'handleStartRecording' },
      })
    }
  }

  const handleStopRecording = async () => {
    try {
      const audioBlob = await stopRecording()
      sendAudioData(audioBlob)
    } catch (err) {
      console.error({
        message: 'Failed to stop recording',
        error: err instanceof Error ? err.message : String(err),
        context: { action: 'handleStopRecording' },
      })
    }
  }

  return (
    <div className="conversation-page">
      <div className="conversation-page-header">
        <h1>Financial Assessment Conversation</h1>
        <p className="home-subtitle">
          Tell us about your financial situation through voice or text
        </p>
        <div className={`connection-status ${isConnected ? 'status-connected' : 'status-disconnected'}`}>
          {isConnected ? 'Connected' : 'Connecting...'}
        </div>
      </div>

      {wsError && (
        <div className="error-banner">
          <div className="websocket-banner-copy">
            <span className="error-icon">!</span>
            <p>{wsError}</p>
          </div>
          {!isConnected && (
            <button
              type="button"
              className="btn btn-secondary websocket-retry-btn"
              onClick={reconnect}
            >
              Retry Connection
            </button>
          )}
        </div>
      )}

      {/* Messages area */}
      <div className="conversation-messages">
        {messages.length === 0 && isConnected && (
          <div className="system-message">
            <div className="message-sender">System</div>
            <div className="message-content">
              <p>Connected. The AI advisor will guide you through the assessment.</p>
            </div>
          </div>
        )}
        {messages.map((msg, index) => (
          <div
            key={index}
            ref={msg.role === 'user' ? lastUserMessageRef : undefined}
            className={`message ${msg.role === 'user' ? 'user-message' : 'assistant-message'}`}
          >
            <div className="message-sender">
              {msg.role === 'user' ? 'You' : 'AI Advisor'}
            </div>
            <div className="message-content">
              <p>{msg.content}</p>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Audio playback (hidden) */}
      <audio ref={audioRef} style={{ display: 'none' }} />

      {/* Voice controls */}
      <div className="conversation-voice-controls">
        {!isRecording ? (
          <button
            className="voice-btn start-btn"
            onClick={handleStartRecording}
            disabled={!isConnected}
          >
            Start Voice
          </button>
        ) : (
          <>
            <button
              className="voice-btn stop-btn"
              onClick={handleStopRecording}
            >
              Stop Voice
            </button>
            <span className="recording-indicator">
              <span className="recording-dot" />
              Recording...
            </span>
          </>
        )}
      </div>

      {/* Text input */}
      <form className="conversation-text-input" onSubmit={handleSendText}>
        <input
          type="text"
          value={textInput}
          onChange={(e) => setTextInput(e.target.value)}
          placeholder="Type your response..."
          disabled={!isConnected || isRecording}
        />
        <button
          type="submit"
          className="conversation-send-btn"
          disabled={!isConnected || !textInput.trim() || isRecording}
        >
          Send
        </button>
      </form>
    </div>
  )
}

export default ConversationPage
